import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import serviceApi from '../../services/serviceApi';
import PackageSelector from './PackageSelector';
import PricePreview from './PricePreview';
import { CUSTOM_PRICING_UI_ENABLED } from './cleaningPricingConfig';

/**
 * SubscriptionPricingPanel — shared catalog/custom pricing UI used by both
 * `CreateSubscriptionModal` and the `CreateServiceClient` wizard (Stage 6c).
 *
 * Responsibilities:
 *   - Render PackageSelector filtered by the location's pricingTier
 *   - Show discount input + billingCycleStart input
 *   - When custom path is selected (no packageCode): reveal listPrice +
 *     visitsIncluded + validityDays + billingCycleEnd inputs
 *   - Run debounced server-side quote for catalog path; client-side preview
 *     for custom path (server recomputes authoritatively on submit)
 *   - Render PricePreview with the live commercials
 *
 * Caller owns form state — controlled via `values` + `onChange(field, value)`.
 * Quote state is managed internally (display-only, never submitted).
 */
const SubscriptionPricingPanel = ({
  locationTier,
  locationId,
  values,
  onChange
}) => {
  const isCustomPath = !values.packageCode;
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Either locationId (modal) or locationTier (wizard, before location persists)
  // is enough to ask for a quote — the panel works in both contexts.
  const canQuote = !!locationId || !!locationTier;

  useEffect(() => {
    if (!canQuote) {
      setQuote(null);
      return undefined;
    }
    if (isCustomPath && (!values.listPrice || parseFloat(values.listPrice) <= 0)) {
      setQuote(null);
      return undefined;
    }

    const handler = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        if (values.packageCode) {
          const res = await serviceApi.getCatalogQuote({
            locationId: locationId || undefined,
            pricingTier: locationId ? undefined : locationTier,
            packageCode: values.packageCode,
            discountAmount: values.discountAmount || 0
          });
          setQuote(res.data);
        } else {
          const list = parseFloat(values.listPrice) || 0;
          const disc = parseFloat(values.discountAmount) || 0;
          const total = +(list - disc).toFixed(2);
          const net = +(total / 1.15).toFixed(2);
          const vat = +(total - net).toFixed(2);
          setQuote({
            commercials: {
              listPrice: list, discountAmount: disc,
              netAmount: net, vatAmount: vat, totalAmount: total,
              currency: 'SAR', vatRate: 15
            }
          });
        }
      } catch (_err) {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [locationId, locationTier, canQuote, values.packageCode, values.discountAmount, values.listPrice, isCustomPath]);

  return (
    <>
      <PackageSelector
        locationTier={locationTier}
        value={values.packageCode}
        onChange={(v) => onChange('packageCode', v)}
      />

      <div className="d-flex gap-3 mb-3">
        <Form.Group style={{ flex: 1 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>بداية الدورة *</Form.Label>
          <Form.Control
            className="touch-input"
            type="date"
            value={values.billingCycleStart}
            onChange={(e) => onChange('billingCycleStart', e.target.value)}
            dir="ltr"
          />
        </Form.Group>
        <Form.Group style={{ flex: 1 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الخصم (ر.س)</Form.Label>
          <Form.Control
            className="touch-input"
            type="number"
            min="0"
            step="0.01"
            value={values.discountAmount}
            onChange={(e) => onChange('discountAmount', e.target.value)}
            placeholder="0"
            dir="ltr"
          />
        </Form.Group>
      </div>

      {/* Custom-path inputs are gated by the UI feature flag. Backend still
          accepts custom payloads (decision #32) — this only hides the entry
          surface. See cleaningPricingConfig.js + CLEANING_SERVICE_FEATURE_MAP.md. */}
      {CUSTOM_PRICING_UI_ENABLED && isCustomPath && canQuote && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: '0.82rem', color: '#856404', marginBottom: 8, fontWeight: 600 }}>
            السعر المخصص (بدون باقة)
          </div>
          <div className="d-flex gap-2 mb-2">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>السعر القياسي *</Form.Label>
              <Form.Control
                className="touch-input"
                type="number"
                min="0"
                step="0.01"
                value={values.listPrice}
                onChange={(e) => onChange('listPrice', e.target.value)}
                placeholder="800"
                dir="ltr"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>عدد الزيارات *</Form.Label>
              <Form.Control
                className="touch-input"
                type="number"
                min="1"
                value={values.visitsIncluded}
                onChange={(e) => onChange('visitsIncluded', e.target.value)}
                placeholder="15"
              />
            </Form.Group>
          </div>
          <div className="d-flex gap-2">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>أيام الصلاحية</Form.Label>
              <Form.Control
                className="touch-input"
                type="number"
                min="1"
                value={values.validityDays}
                onChange={(e) => onChange('validityDays', e.target.value)}
                placeholder="45"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>نهاية الدورة (اختياري)</Form.Label>
              <Form.Control
                className="touch-input"
                type="date"
                value={values.billingCycleEnd}
                onChange={(e) => onChange('billingCycleEnd', e.target.value)}
                dir="ltr"
              />
            </Form.Group>
          </div>
        </div>
      )}

      {(quote || quoteLoading) && (
        <PricePreview commercials={quote?.commercials} loading={quoteLoading} />
      )}

      <Form.Group className="mb-2">
        <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>ملاحظات السعر</Form.Label>
        <Form.Control
          className="touch-input"
          as="textarea"
          rows={2}
          value={values.priceNotes}
          onChange={(e) => onChange('priceNotes', e.target.value)}
          placeholder="اختياري..."
        />
      </Form.Group>
    </>
  );
};

export default SubscriptionPricingPanel;
